import { PagePath } from 'constants/pagePath';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from 'views/auth/useAuth';

type Status = 'authenticating' | 'invalid' | 'timeout';

const HANDSHAKE_TIMEOUT_MS = 15000;

/**
 * 代行ログインの受け口。
 * 管理画面(FPG_Managed)から window.open で開かれ、postMessage(MessageChannel) でセッショントークンを受け取る。
 * - トークンはURL・クエリには一切載らない（メモリ→sessionStorageのみ）
 * - opener が無い / オリジンが一致しない場合は成立しない（直アクセス・他オリジンからの起動を排除）
 */
export const AgencyLogin = () => {
  const navigate = useNavigate();
  const saveToken = useAuthStore((state) => state.saveToken);
  const [status, setStatus] = useState<Status>('authenticating');
  // StrictMode等での二重処理を防止
  const handledRef = useRef(false);

  useEffect(() => {
    const managedOrigin = process.env.REACT_APP_MANAGED_ORIGIN;
    const opener = window.opener as Window | null;

    // 管理画面から window.open で開かれていない＝直アクセス等は不正
    if (!managedOrigin || !opener) {
      setStatus('invalid');
      return;
    }

    let settled = false;
    let port: MessagePort | null = null;
    let nonce: string | null = null;

    const cleanup = () => {
      window.removeEventListener('message', onWindowMessage);
      window.clearTimeout(timer);
      if (port) {
        port.onmessage = null;
      }
    };

    const finish = (token: string, ackNonce: string) => {
      if (settled || handledRef.current) return;
      settled = true;
      handledRef.current = true;
      // 既存のログインと同じ仕組みでトークンを保持（sessionStorage）
      saveToken(token);
      port?.postMessage({ type: 'AGENCY_ACK', nonce: ackNonce });
      cleanup();
      navigate(PagePath.Home);
    };

    const onPortMessage = (event: MessageEvent) => {
      const data = event.data;
      if (
        data?.type === 'AGENCY_TOKEN' &&
        typeof data.token === 'string' &&
        data.nonce === nonce
      ) {
        finish(data.token, data.nonce);
      }
    };

    const onWindowMessage = (event: MessageEvent) => {
      // 管理画面のオリジンからのメッセージのみ受理
      if (event.origin !== managedOrigin) return;
      const data = event.data;
      if (data?.type === 'AGENCY_INIT' && typeof data.nonce === 'string' && event.ports[0]) {
        nonce = data.nonce;
        port = event.ports[0];
        port.onmessage = onPortMessage;
        port.start?.();
      }
    };

    window.addEventListener('message', onWindowMessage);
    const timer = window.setTimeout(() => {
      if (!settled) {
        cleanup();
        setStatus('timeout');
      }
    }, HANDSHAKE_TIMEOUT_MS);

    // 準備完了を opener(管理画面) に通知（targetOrigin を管理画面に限定）
    opener.postMessage({ type: 'AGENCY_READY' }, managedOrigin);

    return cleanup;
  }, [navigate, saveToken]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      {status === 'authenticating' && <p>認証中です。しばらくお待ちください。</p>}
      {status === 'invalid' && (
        <p>無効なアクセスです。管理画面から代行ログインを実行してください。</p>
      )}
      {status === 'timeout' && (
        <p>代行ログインに失敗しました。管理画面からやり直してください。</p>
      )}
    </div>
  );
};
